import {
  ControlModes as StackControlModes,
  Stack,
  StackEdited,
} from '@src/types/Stacks';
import { ControlModeTemplate } from '@src/types/ControlModes';
import { UseMutationResult } from '@tanstack/react-query';

// Define action type enum
export enum StackModalActionType {
  // Stack form actions
  SET_STACK_NAME = 'SET_STACK_NAME',
  SET_NAME_ERROR = 'SET_NAME_ERROR',
  SET_VALIDATION_ERRORS = 'SET_VALIDATION_ERRORS',
  CLEAR_VALIDATION_ERROR = 'CLEAR_VALIDATION_ERROR',
  CLEAR_CONTROL_MODE_VALIDATION_ERROR = 'CLEAR_CONTROL_MODE_VALIDATION_ERROR',
  SHOW_ALERT = 'SHOW_ALERT',
  CLEAR_ALERT = 'CLEAR_ALERT',

  // Control modes actions
  SET_CONTROL_MODES = 'SET_CONTROL_MODES',
  ADD_CONTROL_MODE = 'ADD_CONTROL_MODE',
  DUPLICATE_CONTROL_MODE = 'DUPLICATE_CONTROL_MODE',
  REMOVE_CONTROL_MODE = 'REMOVE_CONTROL_MODE',
  UPDATE_CONTROL_MODE_NAME = 'UPDATE_CONTROL_MODE_NAME',
  UPDATE_CONTROL_MODE_ALIAS = 'UPDATE_CONTROL_MODE_ALIAS',
  REMOVE_CONTROL_MODE_ALIAS = 'REMOVE_CONTROL_MODE_ALIAS',
  UPDATE_CONTROL_MODE_ENABLED = 'UPDATE_CONTROL_MODE_ENABLED',
  UPDATE_FIELD_VALUE = 'UPDATE_FIELD_VALUE',
  DELETE_SECTION = 'DELETE_SECTION',
  TEMPLATES_SELECTED = 'TEMPLATES_SELECTED',

  // Save actions
  SET_IS_SAVING = 'SET_IS_SAVING',
  INITIALIZE_FORM = 'INITIALIZE_FORM',
  RESET_FORM = 'RESET_FORM',

  // Template picker actions
  OPEN_TEMPLATE_PICKER = 'OPEN_TEMPLATE_PICKER',
  CLOSE_TEMPLATE_PICKER = 'CLOSE_TEMPLATE_PICKER',

  // Template confirmation dialog actions
  OPEN_TEMPLATE_CONFIRM = 'OPEN_TEMPLATE_CONFIRM',
  CLOSE_TEMPLATE_CONFIRM = 'CLOSE_TEMPLATE_CONFIRM',

  // Delete confirmation dialog actions
  OPEN_DELETE_CONFIRM = 'OPEN_DELETE_CONFIRM',
  CLOSE_DELETE_CONFIRM = 'CLOSE_DELETE_CONFIRM',
}

// Define the state interface
export interface StackModalState {
  // Stack form state
  stackName: string;
  nameError: string;
  validationErrors: Record<string, string>;
  alertMessage: string | null;

  // Control modes state
  controlModes: StackControlModes[];

  // Save state
  isSaving: boolean;

  // Template picker state
  isTemplatePickerOpen: boolean;
  currentControlModeIndex: number | null;

  // Template confirmation dialog state
  isTemplateConfirmOpen: boolean;
  templatesForConfirmation: ControlModeTemplate[];
  controlModeIndexForConfirmation: number | null;
  templateIdsForConfirmation?: string[];

  // Delete confirmation dialog state
  isDeleteConfirmOpen: boolean;
  controlModeToDelete: number | null;
}

// Define action types
export type StackModalAction =
  // Stack form actions
  | { type: StackModalActionType.SET_STACK_NAME; payload: string }
  | { type: StackModalActionType.SET_NAME_ERROR; payload: string }
  | {
      type: StackModalActionType.SET_VALIDATION_ERRORS;
      payload: Record<string, string>;
    }
  | { type: StackModalActionType.CLEAR_VALIDATION_ERROR; payload: string }
  | {
      type: StackModalActionType.CLEAR_CONTROL_MODE_VALIDATION_ERROR;
      payload: { index: number; field?: string };
    }
  | { type: StackModalActionType.SHOW_ALERT; payload: string }
  | { type: StackModalActionType.CLEAR_ALERT }

  // Control modes actions
  | {
      type: StackModalActionType.SET_CONTROL_MODES;
      payload: StackControlModes[];
    }
  | { type: StackModalActionType.ADD_CONTROL_MODE }
  | { type: StackModalActionType.DUPLICATE_CONTROL_MODE; payload: number }
  | { type: StackModalActionType.REMOVE_CONTROL_MODE; payload: number }
  | {
      type: StackModalActionType.UPDATE_CONTROL_MODE_NAME;
      payload: { index: number; name: string };
    }
  | {
      type: StackModalActionType.UPDATE_CONTROL_MODE_ALIAS;
      payload: { index: number; name: string };
    }
  | { type: StackModalActionType.REMOVE_CONTROL_MODE_ALIAS; payload: number }
  | {
      type: StackModalActionType.UPDATE_CONTROL_MODE_ENABLED;
      payload: { index: number; enabled: boolean };
    }
  | {
      type: StackModalActionType.UPDATE_FIELD_VALUE;
      payload: {
        controlModeIndex: number;
        sectionIndex: number;
        fieldIndex: number;
        newValue: string | number | boolean | null;
      };
    }
  | {
      type: StackModalActionType.DELETE_SECTION;
      payload: { controlModeIndex: number; sectionIndex: number };
    }
  | {
      type: StackModalActionType.TEMPLATES_SELECTED;
      payload: {
        templates: ControlModeTemplate[];
        controlModeIndex: number;
        templateIds?: string[];
      };
    }

  // Save actions
  | { type: StackModalActionType.SET_IS_SAVING; payload: boolean }
  | {
      type: StackModalActionType.INITIALIZE_FORM;
      payload: { stackName: string; controlModes: StackControlModes[] };
    }
  | { type: StackModalActionType.RESET_FORM }

  // Template picker actions
  | { type: StackModalActionType.OPEN_TEMPLATE_PICKER; payload: number }
  | { type: StackModalActionType.CLOSE_TEMPLATE_PICKER }

  // Template confirmation dialog actions
  | {
      type: StackModalActionType.OPEN_TEMPLATE_CONFIRM;
      payload: {
        templates: ControlModeTemplate[];
        controlModeIndex: number;
        templateIds?: string[];
      };
    }
  | { type: StackModalActionType.CLOSE_TEMPLATE_CONFIRM }

  // Delete confirmation dialog actions
  | { type: StackModalActionType.OPEN_DELETE_CONFIRM; payload: number }
  | { type: StackModalActionType.CLOSE_DELETE_CONFIRM };

// Define the hook interface
export interface UseStackModalReducerProps {
  stackId?: string;
  createStack: UseMutationResult<Stack, Error, StackEdited, unknown>;
  updateStack: UseMutationResult<Stack, Error, StackEdited, unknown>;
  onClose: () => void;
  initialStackName?: string;
  initialControlModes?: StackControlModes[];
  isDefault?: boolean;
}

// Define the context interface
export interface StackModalContextType {
  state: {
    stackName: string;
    nameError: string;
    validationErrors: Record<string, string>;
    alertMessage: string | null;
    controlModes: StackControlModes[];
    isSaving: boolean;
    isTemplatePickerOpen: boolean;
    currentControlModeIndex: number | null;
    isTemplateConfirmOpen: boolean;
    templatesForConfirmation: ControlModeTemplate[];
    controlModeIndexForConfirmation: number | null;
    templateIdsForConfirmation?: string[];
    isDeleteConfirmOpen: boolean;
    controlModeToDelete: number | null;
  };

  actions: {
    form: {
      handleNameChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
      handleSave: () => Promise<void>;
    };

    controlMode: {
      handleAddControlMode: () => void;
      handleDuplicateControlMode: (index: number) => void;
      handleControlModeNameChangeWithValidation: (
        index: number,
        name: string,
      ) => void;
      handleControlModeAliasChange: (index: number, name: string) => void;
      handleControlModeAliasRemoval: (index: number) => void;
      handleControlModeEnabledChange: (index: number, enabled: boolean) => void;
      handleDeleteControlMode: (index: number) => void;
      handleOpenDeleteConfirm: (index: number) => void;
      handleCloseDeleteConfirm: () => void;
    };

    field: {
      handleFieldChange: (
        controlModeIndex: number,
        sectionIndex: number,
        fieldIndex: number,
        newValue: string | number | boolean | null,
      ) => void;
      handleDeleteSection: (
        controlModeIndex: number,
        sectionIndex: number,
      ) => void;
    };

    template: {
      handleTemplatesSelected: (
        templates: ControlModeTemplate[],
        controlModeIndex: number,
        templateIds?: string[],
      ) => void;
      handleOpenTemplatePicker: (index: number) => void;
      handleCloseTemplatePicker: () => void;
      handleOpenTemplateConfirm: (
        templates: ControlModeTemplate[],
        controlModeIndex: number,
        templateIds?: string[],
      ) => void;
      handleCloseTemplateConfirm: () => void;
      handleConfirmTemplates: () => void;
    };
  };
}

// Define the stack modal props
export interface StackModalProps {
  stackId?: string;
  open: boolean;
  onClose: () => void;
  isDefault?: boolean;
}
